<script setup lang="ts">
/**
 * The music mute's one piece of feedback, and it is only in the arena.
 *
 * ### The key is the control; this is the confirmation on it
 *
 * `M` toggles the bed from anywhere in the app, and the listener is in
 * `game/audio/music.ts` rather than here or in `game/input.ts` — see
 * `installMusicMuteKey` for why. The settings window is where the control
 * *lives*: `ui/SettingsPanel.vue`'s Audio group carries the music level and its
 * On/Mute pair, and the gear is on the garage's tab strip and behind Escape in a
 * battle. This component exists for the one moment that is not enough — a mute
 * pressed mid-fight, where the settings window is not open and cannot usefully
 * be.
 *
 * ### There used to be a button here, and its removal is the point
 *
 * Outside the arena this drew a chip pinned to the bottom centre — a real
 * button, because there is a cursor on the garage screen and a cursor should be
 * able to press a control. It is gone (owner instruction, 2026-09-02, and a
 * defect before that).
 *
 * The defect: the chip measured 105.9x26.7 at `left: 50%; bottom: 0.85rem`, and
 * the hangar's own *Drag to look around* hint is pinned bottom-centre at
 * `bottom: 0.55rem`. They overlapped by **975px² (106 x 9.2) at every
 * viewport** — a mute label drawn across a hint about the camera. Compact hid
 * the chip for that reason and desktop kept the overlap, which left the app with
 * two answers to one question.
 *
 * The reason it could go rather than move is that **the control already has a
 * home**. A chip whose whole job is to advertise a key binding is worth its
 * pixels only while the binding is nowhere else; the Audio group states `M`
 * beside the Music row it belongs to, which is both the label and the control in
 * the place a player looks for settings.
 *
 * ### Why the arena keeps a flash and not a button
 *
 * `CLAUDE.md` settles this twice already, for the orders panel and for the
 * radar: **aiming holds pointer lock, so the cursor cannot reach a HUD
 * control**, which is why the orders panel is labels rather than buttons and the
 * radar has no pointer handlers at all. A mute button in the arena would be a
 * control that does nothing in the only place it is urgent.
 *
 * So the arena renders nothing at rest and flashes a confirmation for a moment
 * and a half when the state changes. Nothing at rest, because every corner of
 * that HUD is already spoken for — scoreboard top left, speed and settings top
 * right, kill feed under them, mission strips top centre, orders bottom left,
 * radar bottom right, health and reload bottom centre — and a permanent readout
 * for a setting that changes twice a session is not worth taking a slot from any
 * of them. A flash, because a mute you cannot confirm is a mute you press twice.
 *
 * The flash is `pointer-events: none` and is never a target, so it cannot eat a
 * shot the way a HUD button placed over the arena could. It carries the letter,
 * from the audio module, so the key and the caption cannot drift apart.
 */
import { onBeforeUnmount, ref, watch } from 'vue';

import { MUSIC_MUTE_LABEL } from '@/game/audio/music';
import { musicMuted } from '@/state/settings';

const props = defineProps<{
  /** Whether the arena is up. Outside it this component draws nothing at all. */
  arena: boolean;
}>();

/** How long the arena's confirmation stays up, in ms. */
const FLASH_MS = 1600;

const flashing = ref(false);
let timer: ReturnType<typeof setTimeout> | null = null;

watch(musicMuted, () => {
  // Only in the arena. Everywhere else the settings window's own On/Mute pair is
  // what the player just pressed, and a toast confirming a control they are
  // looking at is noise.
  if (!props.arena) return;
  flashing.value = true;
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    flashing.value = false;
    timer = null;
  }, FLASH_MS);
});

onBeforeUnmount(() => {
  if (timer !== null) clearTimeout(timer);
});
</script>

<template>
  <p v-if="props.arena && flashing" class="flash" role="status" data-test="music-flash">
    <span class="glyph" aria-hidden="true">{{ musicMuted ? '♪̸' : '♪' }}</span>
    Music {{ musicMuted ? 'off' : 'on' }}
    <span class="key" aria-hidden="true">{{ MUSIC_MUTE_LABEL }}</span>
  </p>
</template>

<style scoped>
/*
 * Top centre, under the mission strips' band and over nothing that is a target.
 *
 * `z-index: 40` is the garage's overlay tier and is left as it was, even though
 * the chip that used to share this rule is what the number was argued over: the
 * settings window sits at 60 specifically because *this* file paints at 40 and
 * mounts after the garage in `App.vue`, and at 40 the chip drew through that
 * window's footer. A flash in the arena cannot reach the garage's settings
 * window, but the number is load-bearing in the other direction and is written
 * up in `SettingsPanel.vue`'s own `.settings-scrim`.
 */
.flash {
  position: fixed;
  z-index: 40;
  left: 50%;
  top: 0.9rem;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
  padding: 0.3rem 0.55rem;
  /* Never a pointer target: a click here in the arena would be a shot the
     player did not take, and pointer lock means the cursor is not here anyway. */
  pointer-events: none;
  border: 1px solid var(--steel-600, rgba(255, 255, 255, 0.14));
  border-left: 2px solid rgba(232, 114, 44, 0.85);
  border-radius: 2px;
  background: rgba(11, 13, 17, 0.72);
  /* Translucent and blurred rather than opaque, matching the HUD's own panels:
     opaque reads as a box dropped on the scene, and merely translucent is
     illegible over the brightest part of a battlefield. */
  backdrop-filter: blur(6px);
  color: var(--steel-200, #d5dae1);
  font-size: 0.72rem;
  letter-spacing: 0.02em;
  line-height: 1;
  white-space: nowrap;
}

.glyph {
  font-size: 0.85rem;
  color: var(--accent, #e8722c);
}

.key {
  padding: 0.1rem 0.28rem;
  border: 1px solid var(--steel-600, rgba(255, 255, 255, 0.18));
  border-radius: 2px;
  color: var(--steel-400, #8a939f);
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.62rem;
}
</style>
